import { Box, IconButton, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { designTokens } from '../tokens';

export type PageHeaderTitleAction = {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
};

export type PageHeaderTitleProps = {
  leading?: ReactNode;
  title: ReactNode;
  trailingAction?: PageHeaderTitleAction;
  supporting?: ReactNode;
  /** 보조 정보를 제목 옆에 두고 공간이 부족하면 다음 줄로 보냅니다. 기본값은 별도 아래 행입니다. */
  supportingPlacement?: 'below' | 'inline';
  /** 읽기 전용 문서처럼 전체 제목이 필요하면 줄바꿈을 허용합니다. */
  titleOverflow?: 'ellipsis' | 'wrap';
};

const controlSize = {
  xs: `${designTokens.layout.pageHeaderControlSizeCompact}px`,
  sm: `${designTokens.layout.pageHeaderControlSize}px`,
} as const;

const glyphSize = {
  xs: `${designTokens.layout.pageHeaderControlGlyphSizeCompact}px`,
  sm: `${designTokens.layout.pageHeaderControlGlyphSize}px`,
} as const;

/**
 * PageHeader 제목의 leading·title·trailing action과 supporting line 기하를 고정합니다.
 * 기본 supporting line은 별도 grid row를 사용합니다. inline은 제목 열 안에서만 줄바꿈하며
 * leading·trailing action은 보조 정보의 줄 수와 관계없이 제목 첫 줄에 정렬합니다.
 */
export function PageHeaderTitle({ leading, title, trailingAction, supporting, supportingPlacement = 'below', titleOverflow = 'ellipsis' }: PageHeaderTitleProps) {
  const titleColumn = leading ? 2 : 1;
  const trailingColumn = leading ? 3 : 2;
  const inlineSupporting = supportingPlacement === 'inline';
  const titleContent = <Box component="span" sx={{ minWidth: 0, maxWidth: '100%', ...(titleOverflow === 'wrap' ? { whiteSpace: 'normal', overflowWrap: 'anywhere' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>
    {title}
  </Box>;
  const supportingContent = supporting ? <Box
    component="span"
    data-testid="page-header-title-supporting"
    sx={{
      ...(inlineSupporting
        ? { flexBasis: { xs: '100%', sm: 'auto' }, maxWidth: '100%', overflowWrap: 'anywhere', whiteSpace: 'normal' }
        : { gridColumn: `${titleColumn} / -1`, gridRow: 2 }),
      minWidth: 0,
      display: 'block',
    }}
  >{supporting}</Box> : null;

  return <Box
    component="span"
    data-testid="page-header-title-layout"
    sx={{
      display: 'grid',
      gridTemplateColumns: leading
        ? { xs: `${controlSize.xs} minmax(0, 1fr) auto`, sm: `${controlSize.sm} minmax(0, 1fr) auto` }
        : 'minmax(0, 1fr) auto',
      gridTemplateRows: inlineSupporting ? 'auto' : 'auto auto',
      [designTokens.media.touchTarget]: {
        ...(leading ? { gridTemplateColumns: `${designTokens.layout.touchTargetMinSize}px minmax(0, 1fr) auto` } : undefined),
        '& [data-testid="page-header-title-leading"], & [data-testid="page-header-title-trailing"]': {
          width: designTokens.layout.touchTargetMinSize,
          height: designTokens.layout.touchTargetMinSize,
          minWidth: designTokens.layout.touchTargetMinSize,
        },
        '& [data-testid="page-header-title-content"]': { minHeight: designTokens.layout.touchTargetMinSize },
      },
      columnGap: { xs: 0.75, sm: 1 },
      rowGap: supporting && !inlineSupporting ? 0.75 : 0,
      width: 'fit-content',
      maxWidth: '100%',
      minWidth: 0,
      alignItems: 'center',
    }}
  >
    {leading ? <Box
      component="span"
      data-testid="page-header-title-leading"
      sx={{
        gridColumn: 1,
        gridRow: 1,
        ...(inlineSupporting ? { alignSelf: 'start' } : {}),
        width: controlSize,
        height: controlSize,
        minWidth: controlSize,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: glyphSize,
        '& svg': { fontSize: 'inherit' },
      }}
    >{leading}</Box> : null}
    <Box
      component="span"
      data-testid="page-header-title-content"
      sx={{
        gridColumn: titleColumn,
        gridRow: 1,
        minWidth: 0,
        minHeight: controlSize,
        display: 'inline-flex',
        alignItems: 'center',
        ...(inlineSupporting ? { flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.75 } : {}),
      }}
    >
      {inlineSupporting ? <Box component="span" sx={{ minWidth: 0, maxWidth: '100%', minHeight: controlSize, display: 'inline-flex', alignItems: 'center', [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize } }}>
        {titleContent}
      </Box> : titleContent}
      {inlineSupporting ? supportingContent : null}
    </Box>
    {trailingAction ? <Box
      component="span"
      data-testid="page-header-title-trailing"
      sx={{
        gridColumn: trailingColumn,
        gridRow: 1,
        ...(inlineSupporting ? { alignSelf: 'start' } : {}),
        width: controlSize,
        height: controlSize,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Tooltip title={trailingAction.label}>
        <span>
          <IconButton
            aria-label={trailingAction.label}
            disabled={trailingAction.disabled}
            onClick={trailingAction.onSelect}
          >{trailingAction.icon}</IconButton>
        </span>
      </Tooltip>
    </Box> : null}
    {!inlineSupporting ? supportingContent : null}
  </Box>;
}
